import { useState, useEffect, useCallback } from "react";
import { auth, db, sendVerificationEmailFn, sendWelcomeEmailFn } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";

import { LoadingScreen, SplashScreen } from "../components/shared";
import Login from "../components/Auth/Login";
import { EmailVerifyPendingScreen, ResetPasswordScreen, VerifyEmailActionScreen } from "../components/Auth/VerificationCode";
import FindTrip from "../components/Trips/FindTrip";
import PostTrip from "../components/Trips/PostTrip";
import TripsScreen from "../components/Trips/TripsScreen";
import TripDetail from "../components/Trips/TripDetails";
import ChatScreen from "../components/Chat/ChatScreen";
import Alerts from "../components/Alerts/Alerts";
import Profile from "../components/Profile/Profile";
import { BottomNav, DesktopTopNav } from "../components/Navigation/BottomNav";
import DesktopLayout from "../components/Desktop/DesktopLayout";

// ── Home Screen (main app shell with auth state) ──────────────────────────────
const HomeScreen = () => {
  const [authState,   setAuthState]   = useState("loading"); // loading | unauthed | verify | authed
  const [userProfile,    setUserProfile]    = useState(null);
  const [tab,            setTab]            = useState("find");
  const [detail,         setDetail]         = useState(null);
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [chatCount,      setChatCount]      = useState(0);
  const [tripsSubview,   setTripsSubview]   = useState(null);
  const [showSplash,     setShowSplash]     = useState(!sessionStorage.getItem('splashPlayed'));
  const [openChatReq,    setOpenChatReq]    = useState(null);
  const [openLostChat,   setOpenLostChat]   = useState(null);
  const [chatActive,     setChatActive]     = useState(false);
  const [isDesktop,      setIsDesktop]      = useState(() => window.innerWidth > 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hideSplash = useCallback(() => setShowSplash(false), []);

  const loadProfile = async (firebaseUser) => {
    const snap = await getDoc(doc(db, "users", firebaseUser.uid));
    if (snap.exists()) {
      const priv = await getDoc(doc(db, "users", firebaseUser.uid, "private", "info"));
      setUserProfile({ id: firebaseUser.uid, email: firebaseUser.email, ...snap.data(), ...(priv.exists() ? priv.data() : {}) });
      setAuthState("authed");
    } else {
      await signOut(auth);
      setAuthState("unauthed");
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async firebaseUser => {
      if (!firebaseUser) { setAuthState("unauthed"); setUserProfile(null); return; }
      // SECURITY: gate on Firebase Auth's emailVerified — the authoritative source.
      if (!firebaseUser.emailVerified) { setAuthState("verify"); return; }
      const snap = await getDoc(doc(db, "users", firebaseUser.uid));
      if (!snap.exists()) { await signOut(auth); setAuthState("unauthed"); return; }
      const priv = await getDoc(doc(db, "users", firebaseUser.uid, "private", "info"));
      setUserProfile({ id: firebaseUser.uid, email: firebaseUser.email, ...snap.data(), ...(priv.exists() ? priv.data() : {}) });
      setAuthState("authed");
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Firebase action links (?mode=…&oobCode=…) redirected to https://igonow.xyz
  const urlParams  = new URLSearchParams(window.location.search);
  const urlMode    = urlParams.get("mode");
  const urlOobCode = urlParams.get("oobCode");
  const isResetPath = window.location.pathname === "/reset-password";

  if ((urlOobCode && urlMode === "resetPassword") || (isResetPath && urlOobCode)) {
    return <ResetPasswordScreen oobCode={urlOobCode}/>;
  }

  if (urlOobCode && urlMode === "verifyEmail") {
    return (
      <VerifyEmailActionScreen
        oobCode={urlOobCode}
        onVerified={async () => {
          if (!auth.currentUser) return;
          const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (snap.exists()) {
            const profile = snap.data();
            const priv = await getDoc(doc(db, "users", auth.currentUser.uid, "private", "info"));
            setUserProfile({ id: auth.currentUser.uid, email: auth.currentUser.email, ...profile, ...(priv.exists() ? priv.data() : {}) });
            setAuthState("authed");
            if (!profile.welcomeEmailSent) {
              sendWelcomeEmailFn({ email: auth.currentUser.email, name: profile.name }).catch(() => {});
              updateDoc(doc(db, "users", auth.currentUser.uid), { welcomeEmailSent: true }).catch(() => {});
            }
          }
        }}
      />
    );
  }

  const changeTab = t => { setTab(t); setDetail(null); };

  const navigateTo = (toTab, subview) => {
    if (toTab === "chat" && subview?.chatId) {
      setOpenLostChat(subview);
      changeTab("chat");
      return;
    }
    changeTab(toTab);
    if (subview) setTripsSubview(subview);
  };

  // ── Build content based on auth state (NO early returns — splash must stay
  //    at a stable tree position so React never unmounts it mid-animation) ──
  let content;
  if (authState === "loading") {
    content = !showSplash ? <LoadingScreen/> : null;
  } else if (authState === "unauthed") {
    content = <Login/>;
  } else if (authState === "verify") {
    content = (
      <EmailVerifyPendingScreen
        email={auth.currentUser?.email || ""}
        onResend={async () => {
          if (auth.currentUser) await sendVerificationEmailFn({ email: auth.currentUser.email });
        }}
        onContinue={async () => {
          if (!auth.currentUser) return;
          await auth.currentUser.reload();
          if (!auth.currentUser.emailVerified) throw new Error("not verified");
          const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (snap.exists()) {
            const profile = snap.data();
            const priv = await getDoc(doc(db, "users", auth.currentUser.uid, "private", "info"));
            setUserProfile({ id: auth.currentUser.uid, email: auth.currentUser.email, ...profile, ...(priv.exists() ? priv.data() : {}) });
            setAuthState("authed");
            if (!profile.welcomeEmailSent) {
              sendWelcomeEmailFn({ email: auth.currentUser.email, name: profile.name }).catch(() => {});
              updateDoc(doc(db, "users", auth.currentUser.uid), { welcomeEmailSent: true }).catch(() => {});
            }
          }
        }}
        onBack={() => { signOut(auth); setAuthState("unauthed"); }}
      />
    );
  } else if (!userProfile) {
    content = <LoadingScreen/>;
  } else if (isDesktop) {
    content = <DesktopLayout currentUser={userProfile} setUserProfile={setUserProfile}/>;
  } else {
    const screens = {
      find:    <FindTrip    currentUser={userProfile} setDetail={setDetail}/>,
      post:    <PostTrip    currentUser={userProfile}/>,
      trips:   <TripsScreen currentUser={userProfile} forceView={tripsSubview} onViewApplied={() => setTripsSubview(null)} onNavigateChat={() => changeTab("chat")} onOpenChat={(req) => { setOpenChatReq(req); changeTab("chat"); }}/>,
      chat:    <ChatScreen  currentUser={userProfile} openRequest={openChatReq} onOpenRequestHandled={() => setOpenChatReq(null)} onActiveChange={setChatActive} openLostChat={openLostChat} onLostChatHandled={() => setOpenLostChat(null)}/>,
      notifs:  <Alerts      currentUser={userProfile} onCount={setUnreadCount} onChatCount={setChatCount} onNavigate={navigateTo}/>,
      profile: <Profile     currentUser={userProfile} onLogout={() => signOut(auth)} onAvatarUpdate={url => setUserProfile(p => ({ ...p, avatar: url }))} onPhoneUpdate={phone => setUserProfile(p => ({ ...p, phone }))} onNavigate={navigateTo}/>,
    };
    content = (
      <div className="app-shell" style={{ maxWidth: 430, margin: "0 auto", height: "100dvh", position: "relative", overflow: "hidden", background: "var(--paper)" }}>
        <DesktopTopNav tab={tab} setTab={changeTab} unreadCount={unreadCount} chatCount={chatCount} user={userProfile}/>
        <div className="desktop-main" style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          <div key={tab} className="ascreen" style={{ height: "100%", position: "relative" }}>{screens[tab]}</div>
          {detail && <TripDetail trip={detail} currentUser={userProfile} onBack={() => setDetail(null)}/>}
        </div>
        {!detail && !chatActive && <BottomNav tab={tab} setTab={changeTab} unreadCount={unreadCount} chatCount={chatCount}/>}
      </div>
    );
  }

  // Splash is ALWAYS the first child — same tree position regardless of auth
  return (
    <>
      {showSplash && <SplashScreen onDone={hideSplash}/>}
      {content}
    </>
  );
};

export default HomeScreen;
